/**
 * Centralised API client for the Workflow Engine + Provider Engine.
 *
 * Returns plain JSON (no Axios wrapping). All RBAC is enforced server-side;
 * this layer just shuttles bytes. Components must NOT hardcode states,
 * transitions, mode names, or queue names — those come from the backend.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const h = (getAuthHeaders) => ({ headers: getAuthHeaders() });

// ---- State machines & queues meta ------------------------------------------

export const fetchStateMachines = async (getAuthHeaders) =>
  (await axios.get(`${API}/operations/_meta/states`, h(getAuthHeaders))).data;

export const fetchQueues = async (getAuthHeaders) =>
  (await axios.get(`${API}/operations/queues`, h(getAuthHeaders))).data;

export const fetchQueueItems = async (getAuthHeaders, name) =>
  (await axios.get(`${API}/operations/queues/${name}`, h(getAuthHeaders))).data;

// ---- Operations ------------------------------------------------------------

export const listOperations = async (getAuthHeaders, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== null) qs.set(k, v); });
  return (await axios.get(`${API}/operations?${qs}`, h(getAuthHeaders))).data;
};

export const getOperation = async (getAuthHeaders, id) =>
  (await axios.get(`${API}/operations/${id}`, h(getAuthHeaders))).data;

export const createOperation = async (getAuthHeaders, body) =>
  (await axios.post(`${API}/operations`, body, h(getAuthHeaders))).data;

export const transitionOperation = async (getAuthHeaders, id, target_state, note) =>
  (await axios.post(`${API}/operations/${id}/transition`, { target_state, note }, h(getAuthHeaders))).data;

export const assignProvider = async (getAuthHeaders, id, provider_id) =>
  (await axios.post(`${API}/operations/${id}/assign`, { provider_id }, h(getAuthHeaders))).data;

export const candidatesFor = async (getAuthHeaders, id) =>
  (await axios.get(`${API}/operations/${id}/candidates`, h(getAuthHeaders))).data;

export const operationAudit = async (getAuthHeaders, id) =>
  (await axios.get(`${API}/operations/${id}/audit`, h(getAuthHeaders))).data;

// ---- Providers -------------------------------------------------------------

export const listProviders = async (getAuthHeaders, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
  return (await axios.get(`${API}/providers?${qs}`, h(getAuthHeaders))).data;
};

export const getProvider = async (getAuthHeaders, id) =>
  (await axios.get(`${API}/providers/${id}`, h(getAuthHeaders))).data;

export const createProvider = async (getAuthHeaders, body) =>
  (await axios.post(`${API}/providers`, body, h(getAuthHeaders))).data;

export const updateProvider = async (getAuthHeaders, id, patch) =>
  (await axios.patch(`${API}/providers/${id}`, patch, h(getAuthHeaders))).data;

export const deleteProvider = async (getAuthHeaders, id) =>
  (await axios.delete(`${API}/providers/${id}`, h(getAuthHeaders))).data;

// ---- Documents -------------------------------------------------------------

export const listDocuments = async (getAuthHeaders, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
  return (await axios.get(`${API}/documents?${qs}`, h(getAuthHeaders))).data;
};

export const uploadDocument = async (getAuthHeaders, body) =>
  (await axios.post(`${API}/documents`, body, h(getAuthHeaders))).data;

export const verifyDocument = async (getAuthHeaders, id, status, note) =>
  (await axios.patch(`${API}/documents/${id}/verify`, { status, note }, h(getAuthHeaders))).data;

export const deleteDocument = async (getAuthHeaders, id) =>
  (await axios.delete(`${API}/documents/${id}`, h(getAuthHeaders))).data;

export const documentDownloadUrl = (storage_key) =>
  `${API}/documents/raw/${storage_key}`;
